/**
 * Socle d'IHM commun a la flotte (`@santvia/ui-socle`, CDC § « Le socle d'IHM est commun a toute la flotte »).
 * Klavox CONSOMME les jetons `--sk-*` (palette claire/sombre, rayons, verre, anneau de focus, accessibilite
 * systeme) : il ne les redefinit jamais, il les pose une fois dans le document puis sa feuille s'y adosse.
 * Il consomme aussi les composants du socle (bulles d'avis, « Travail en cours », barre d'etat, cartes d'avis) et
 * leur fournit ses libelles `socle.*` : le socle n'en porte aucun.
 */
import {
  BUSY_INDICATOR_CSS,
  NOTICE_BUBBLES_CSS,
  NOTICE_CARD_CSS,
  STATUS_BAR_CSS,
  TITLE_BAR_CSS,
  type SocleKey,
  type SocleTranslate,
} from '@santvia/ui-socle';
import { DESIGN_TOKENS_CSS, TEXT_ZOOM_STEPS } from '@santvia/ui-socle/tokens';
import { escapeHtml } from './render-utils.js';

/** Marque de la balise `<style>` des jetons : sert a ne la poser qu'une fois, et aux tests a la retrouver. */
export const SOCLE_TOKENS_MARK = 'tokens';
/** Marque de la balise `<style>` des composants du socle. */
export const SOCLE_COMPONENTS_MARK = 'components';
export const SOCLE_COMPONENTS_CSS = `${NOTICE_BUBBLES_CSS}${BUSY_INDICATOR_CSS}${STATUS_BAR_CSS}${NOTICE_CARD_CSS}${TITLE_BAR_CSS}`;

/** Cle `localStorage` du palier de taille du texte : une preference d'affichage, jamais un secret. */
export const CLE_ZOOM_TEXTE = 'klavox:text-zoom';
export const ZOOM_TEXTE_DEFAUT = 100;

/**
 * Le palier de taille du texte a poser en `data-text-zoom` sur `<html>` : un des `TEXT_ZOOM_STEPS` du socle, seuls
 * paliers auxquels sa feuille de jetons repond. Toute autre valeur (absente, trafiquee) retombe a 100 % : un palier
 * inconnu poserait un attribut qu'aucune regle ne lit, et le reglage afficherait une taille qui n'est pas la vraie.
 */
export function palierZoomTexte(brut: string | null): number {
  const palier = Number(brut);
  return TEXT_ZOOM_STEPS.includes(palier) ? palier : ZOOM_TEXTE_DEFAUT;
}

/**
 * Pose les jetons du socle en TETE du `<head>`, suivis des feuilles de ses composants : la feuille de Klavox,
 * chargee apres, les lit sans pouvoir passer devant. Idempotent (un remontage de l'app ne duplique pas la balise).
 */
export function installSocleTokens(doc: Document): void {
  if (doc.head.querySelector(`style[data-socle="${SOCLE_TOKENS_MARK}"]`) !== null) return;
  const tokens = doc.createElement('style');
  tokens.dataset.socle = SOCLE_TOKENS_MARK;
  tokens.textContent = DESIGN_TOKENS_CSS;
  const components = doc.createElement('style');
  components.dataset.socle = SOCLE_COMPONENTS_MARK;
  components.textContent = SOCLE_COMPONENTS_CSS;
  doc.head.prepend(tokens, components);
}

/** Libelles de Klavox pour le socle. Le type exige TOUTES les cles de `SOCLE_KEYS` : une cle oubliee ne compile pas. */
const LIBELLES_SOCLE: Readonly<Record<SocleKey, string>> = {
  'socle.notice.close': 'Fermer',
  'socle.notice.report': 'Signaler ce probleme',
  'socle.busy': 'Travail en cours : {task}…',
  'socle.busy.generic': 'Travail en cours…',
  'socle.diagnostics.title': 'Envoyer le journal au support',
  'socle.diagnostics.intro': 'Voici tout ce qui serait envoye. Rien ne part sans votre accord, et aucun secret du coffre n’y figure.',
  'socle.diagnostics.comment': 'Votre commentaire (facultatif)',
  'socle.diagnostics.preview': 'Contenu du journal',
  'socle.diagnostics.loading': 'Chargement du journal…',
  'socle.diagnostics.empty': 'Le journal est vide.',
  'socle.diagnostics.compose': 'Preparer l’e-mail a {address}',
  'socle.diagnostics.save': 'Enregistrer le journal',
  'socle.diagnostics.close': 'Fermer',
  'socle.diagnostics.outcome.noAccount': 'Aucune messagerie disponible : enregistrez le journal et joignez-le vous-meme.',
  'socle.diagnostics.outcome.saved': 'Journal enregistre.',
  'socle.diagnostics.mailSubject': 'Klavox {version} : journal de diagnostic',
  'socle.diagnostics.mailBody': 'Bonjour,\n\nVous trouverez ci-joint le journal {file} (Klavox {version}).\n',
  'socle.status.label': 'Barre d’etat',
  'socle.status.activity': 'Activite',
  'socle.status.idle': 'Tout est a jour',
  'socle.status.running': '{count} en cours',
  'socle.status.failed': '{count} refusee(s)',
  'socle.status.empty': 'Rien en cours.',
  'socle.status.failedTag': 'refusee',
  'socle.titlebar.label': 'Barre de titre',
  // Mention IA (AI Act art. 50) et bulle d'attente de l'assistant, socle >= 0.9 : Klavox ne monte aujourd'hui ni
  // `markAiGenerated` ni `mountAssistantThinking`, mais le jour ou il le fait, le libelle est deja la, et juste.
  'socle.ai.mention': 'Reponse generee par une IA.',
  'socle.ai.mention.provider': 'Reponse generee par une IA ({provider}).',
  'socle.ai.mention.kind': '{kind} generee par une IA.',
  'socle.ai.mention.kind.provider': '{kind} generee par une IA ({provider}).',
  'socle.assistant.thinking.1': 'Je lis votre demande…',
  'socle.assistant.thinking.2': 'Je reflechis…',
  'socle.assistant.thinking.3': 'Je cherche la meilleure reponse…',
  'socle.assistant.thinking.4': 'Je redige la reponse…',
  'socle.assistant.thinking.5': 'Reflexion presque terminee…',
  'socle.assistant.thinking.6': 'Encore un instant…',
};

/** La `SocleTranslate` de Klavox : `{nom}` remplace par le parametre du meme nom. */
export const traduireSocle: SocleTranslate = (key, params = {}) =>
  LIBELLES_SOCLE[key].replace(/\{(\w+)\}/gu, (brut, nom: string) => params[nom] ?? brut);

/**
 * Avis de Klavox que le socle transforme en bulles : les confirmations (`flash`) et les echecs d'operation
 * (creation d'alias, verification des fuites). Les erreurs de saisie restent a cote de leur champ.
 */
export const SELECTEUR_BULLES = '[data-role="flash"], [data-role="operation-error"]';

/**
 * Un echec d'operation, rendu pour devenir une bulle du socle. JAMAIS `role="alert"` : le socle y ajouterait
 * « Signaler ce probleme » (`app.diagnostics.open`), que Klavox ne branche pas (ni journal ni adresse de support) --
 * un bouton mort. L'echec est annonce par `aria-live="assertive"` ; sans `role="status"`, il ne s'efface pas seul.
 */
export function echecOperation(message: string): string {
  return `<div data-role="operation-error" aria-live="assertive">${escapeHtml(message)}</div>`;
}

// Un avertissement qui doit RESTER a cote de ce qu'il explique (cle revelee une seule fois, mode voyage actif,
// enregistrements ecartes, lien invalide) n'est pas une bulle : il porte la carte d'avis du socle (`NOTICE_CARD_CSS`),
// `data-sk-notice="info|warning|alert"`, dans la vue.
